import React, { useEffect, useMemo, useState } from "react";
import LayerAdmin from "../LayerAdmin";
import PaginationSearch from "../components/PaginationAndSearch";
import { toast, ToastContainer } from "react-toastify";
import { useDebounce } from "../hooks/debounce";
import URL_MAPPING from "../../../routes/constants";
import { useNavigate } from "react-router-dom";
import { getAllTrainingVideos } from "../api/trainingVideos";
import TrainingVideosTable from "../components/TrainingVideosTable";

const TrainingVideos = () => {
  const [tableData, setTableData] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [refetch, setRefetch] = useState(false);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");

  const navigate = useNavigate();
  const debounce = useDebounce(search);

  const getTableData = async () => {
    try {
      setLoading(true);
      const params: { search?: string } = {};
      if (search.length > 0) params.search = search;
      const response = await getAllTrainingVideos(page, 20, params);
      const items = Array.isArray(response?.items)
        ? response.items
        : Array.isArray(response)
          ? response
          : [];

      if (!response || (!Array.isArray(response?.items) && !Array.isArray(response))) {
        toast.error("No data received or malformed response");
        setTableData({ items: [], page: 1, size: 20, total: 0 });
        return;
      }

      setTableData(
        Array.isArray(response)
          ? { items: response, page: 1, size: response.length, total: response.length }
          : { ...response, items }
      );
      if (response.page) setPage(response.page);
    } catch (error: any) {
      console.error("Failed to fetch training videos:", error);
      const detail = error?.response?.data?.detail;
      if (Array.isArray(detail)) {
        detail.forEach((err) => toast.error(err.msg));
      } else {
        toast.error(detail?.msg || detail || "Failed to fetch training videos.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePaginationNext = () => {
    if (loading) return;
    setPage((prev) => prev + 1);
  };

  const handlePaginationPrev = () => {
    if (loading) return;
    setPage((prev) => Math.max(prev - 1, 1));
  };

  const totalPages = useMemo(() => {
    if (!tableData?.total || !tableData?.size) return 0;
    return Math.ceil(tableData.total / tableData.size);
  }, [tableData?.total, tableData?.size]);

  useEffect(() => {
    getTableData();
  }, [page, debounce, refetch]);

  return (
    <LayerAdmin>
      <ToastContainer />
      <PaginationSearch
        page={page}
        size={totalPages}
        handlePaginationNext={handlePaginationNext}
        handlePaginationPrev={handlePaginationPrev}
        search={search}
        setSearch={setSearch}
        showSearch={true}
      />
      <div className="mb-4 px-5 flex items-end justify-between">
        <h3 className="text-xl font-bold text-gray-700">Training Videos</h3>
        <button
          onClick={() => navigate(URL_MAPPING.ADDTRAININGVIDEO)}
          className="bg-teal-500 text-white px-6 py-2 rounded hover:bg-teal-600 flex items-center gap-2"
        >
          Upload Video
        </button>
      </div>
      <TrainingVideosTable
        tableData={tableData}
        loading={loading}
        refetch={refetch}
        setRefetch={setRefetch}
      />
    </LayerAdmin>
  );
};

export default TrainingVideos;
